import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import jwtUtil from '@/utils/jwtUtil'
import vueConfig from '@/../vue.config'
import { PRIVILEGES, ROLES } from '@/utils/consts'
import { hasPrivilege } from '@/utils/userUtils'

Vue.use(VueRouter)

const routes = [
    {
        path: '/test-list',
        name: 'Test List',
        component: () => import('@/routes/TestListRoute.vue'),
        meta: { requiresPrivilege: PRIVILEGES.TESTLIST_READ_PRIVILEGE }
    },
    {
        path: '/test-list/create',
        name: 'Test List Create',
        component: () => import('@/routes/TestListCreateRoute.vue'),
        meta: { requiresPrivilege: PRIVILEGES.TESTLIST_CREATE_PRIVILEGE }
    },
    {
        path: '/test-list/:testListId/edit',
        name: 'Test List Edit',
        component: () => import('@/routes/TestListEditRoute.vue'),
        meta: { requiresPrivilege: PRIVILEGES.TESTLIST_UPDATE_PRIVILEGE }
    },
    {
        path: '/viber',
        name: 'Viber Campaigns',
        component: () => import('@/routes/ViberCampaignsListRoute.vue'),
        meta: { requiresPrivilege: PRIVILEGES.VIBER_CAMPAIGN_READ_PRIVILEGE }
    },
    {
        path: '/viber/create',
        name: 'Viber Create Campaign',
        component: () => import('@/routes/ViberCampaignCreateRoute.vue'),
        meta: { requiresPrivilege: PRIVILEGES.VIBER_CAMPAIGN_CREATE_PRIVILEGE }
    },
    {
        path: '/viber/:campaignId?',
        name: 'Viber Campaign Details',
        component: () => import('@/routes/ViberCampaignInfoRoute.vue'),
        meta: { requiresPrivilege: PRIVILEGES.VIBER_CAMPAIGN_READ_PRIVILEGE }
    },
    {
        path: '/viber/:campaignId/edit',
        name: 'Viber Campaign Edit',
        component: () => import('@/routes/ViberCampaignEditRoute.vue'),
        meta: { requiresPrivilege: PRIVILEGES.VIBER_CAMPAIGN_UPDATE_PRIVILEGE }
    },
    {
        path: '/sms',
        name: 'SMS Campaigns',
        component: () => import('@/routes/SMSCampaignsListRoute.vue'),
        meta: { requiresPrivilege: PRIVILEGES.SMS_CAMPAIGN_READ_PRIVILEGE }
    },
    {
        path: '/sms/create',
        name: 'SMS Create Campaign',
        component: () => import('@/routes/SMSCampaignCreateRoute.vue'),
        meta: { requiresPrivilege: PRIVILEGES.SMS_CAMPAIGN_CREATE_PRIVILEGE }
    },
    {
        path: '/sms/:campaignId?',
        name: 'SMS Details',
        component: () => import('@/routes/SMSCampaignInfoRoute.vue'),
        meta: { requiresPrivilege: PRIVILEGES.SMS_CAMPAIGN_READ_PRIVILEGE }
    },
    {
        path: '/sms/:campaignId/edit',
        name: 'SMS Campaign Edit',
        component: () => import('@/routes/SMSCampaignEditRoute.vue'),
        meta: { requiresPrivilege: PRIVILEGES.SMS_CAMPAIGN_UPDATE_PRIVILEGE }
    },
    {
        path: '/recurring-viber',
        name: 'Recurring Viber Campaigns',
        component: () => import('@/routes/recurring/RecurringViberCampaignsListRoute.vue'),
        meta: { requiresPrivilege: PRIVILEGES.RECURRING_VIBER_CAMPAIGN_READ_PRIVILEGE }
    },
    {
        path: '/recurring-viber/create',
        name: 'Recurring Viber Create Campaign',
        component: () => import('@/routes/recurring/RecurringViberCampaignCreateRoute.vue'),
        meta: { requiresPrivilege: PRIVILEGES.RECURRING_VIBER_CAMPAIGN_CREATE_PRIVILEGE }
    },
    {
        path: '/recurring-viber/:campaignId?',
        name: 'Recurring Viber Campaign Details',
        component: () => import('@/routes/recurring/RecurringViberCampaignInfoRoute.vue'),
        meta: { requiresPrivilege: PRIVILEGES.RECURRING_VIBER_CAMPAIGN_READ_PRIVILEGE }
    },
    {
        path: '/recurring-viber/:campaignId/edit',
        name: 'Recurring Viber Edit',
        component: () => import('@/routes/recurring/RecurringViberCampaignEditRoute.vue'),
        meta: { requiresPrivilege: PRIVILEGES.RECURRING_VIBER_CAMPAIGN_UPDATE_PRIVILEGE }
    },
    {
        path: '/recurring-sms',
        name: 'Recurring SMS Campaigns',
        component: () => import('@/routes/recurring/RecurringSMSCampaignsListRoute.vue'),
        meta: { requiresPrivilege: PRIVILEGES.RECURRING_SMS_CAMPAIGN_READ_PRIVILEGE }
    },
    {
        path: '/recurring-sms/create',
        name: 'Recurring SMS Create Campaign',
        component: () => import('@/routes/recurring/RecurringSMSCampaignCreateRoute.vue'),
        meta: { requiresPrivilege: PRIVILEGES.RECURRING_SMS_CAMPAIGN_CREATE_PRIVILEGE }
    },
    {
        path: '/recurring-sms/:campaignId?',
        name: 'Recurring SMS Details',
        component: () => import('@/routes/recurring/RecurringSMSCampaignInfoRoute.vue'),
        meta: { requiresPrivilege: PRIVILEGES.RECURRING_SMS_CAMPAIGN_READ_PRIVILEGE }
    },
    {
        path: '/recurring-sms/:campaignId/edit',
        name: 'Recurring SMS Edit',
        component: () => import('@/routes/recurring/RecurringSMSCampaignEditRoute.vue'),
        meta: { requiresPrivilege: PRIVILEGES.RECURRING_SMS_CAMPAIGN_UPDATE_PRIVILEGE }
    },
    {
        path: '/login',
        name: 'Login',
        component: () => import('@/routes/LoginRoute.vue')
    },
    {
        path: '/users',
        name: 'Users',
        component: () => import('@/routes/UserListRoute.vue'),
        meta: {
            requiresPrivilege: PRIVILEGES.USER_READ_PRIVILEGE,
            requiresAdmin: true
        }
    },
    {
        path: '/users/create/:id?',
        name: 'User Create',
        component: () => import('@/routes/CreateUserRoute.vue'),
        meta: {
            requiresPrivilege: PRIVILEGES.USER_CREATE_PRIVILEGE,
            requiresAdmin: true
        }
    },
    {
        path: '/active-hours',
        name: 'Active Hours',
        component: () => import('@/routes/ActiveHoursRoute.vue')
    },
    {
        path: '/active-hours/create',
        name: 'Active Hours Create',
        component: () => import('@/routes/CreateActiveHoursRoute.vue'),
        meta: {
            requiresPrivilege: PRIVILEGES.USER_CREATE_PRIVILEGE
        }
    },
    {
        path: '/users/:userId',
        name: 'User Details',
        component: () => import('@/routes/UserInfoRoute.vue'),
        meta: {
            requiresPrivilege: PRIVILEGES.USER_UPDATE_PRIVILEGE,
            requiresAdmin: true
        }
    },
    {
        path: '/transactional-viber',
        name: 'Transactional Viber',
        component: () => import('@/routes/transactional/ListRoute.vue'),
        meta: { requiresPrivilege: PRIVILEGES.TRANSACTIONAL_VIBER_READ_PRIVILEGE }
    },
    {
        path: '/transactional-sms',
        name: 'Transactional SMS',
        component: () => import('@/routes/transactional/ListRoute.vue'),
        meta: { requiresPrivilege: PRIVILEGES.TRANSACTIONAL_SMS_READ_PRIVILEGE }
    },
    {
        path: '/errors',
        name: 'Errors',
        component: () => import('@/routes/ErrorListRoute.vue'),
        meta: { requiresPrivilege: PRIVILEGES.ERROR_READ_PRIVILEGE }
    }
]

const router = new VueRouter({
    mode: 'history',
    base: vueConfig.publicPath,
    routes
})

function getDefaultPath(privileges) {
    if (hasPrivilege(PRIVILEGES.VIBER_CAMPAIGN_READ_PRIVILEGE, privileges)) {
        return '/viber'
    }
    if (hasPrivilege(PRIVILEGES.SMS_CAMPAIGN_READ_PRIVILEGE, privileges)) {
        return '/sms'
    }
    if (hasPrivilege(PRIVILEGES.RECURRING_VIBER_CAMPAIGN_READ_PRIVILEGE, privileges)) {
        return '/recurring-viber'
    }
    if (hasPrivilege(PRIVILEGES.RECURRING_SMS_CAMPAIGN_READ_PRIVILEGE, privileges)) {
        return '/recurring-sms'
    }
    if (hasPrivilege(PRIVILEGES.TRANSACTIONAL_VIBER_READ_PRIVILEGE, privileges)) {
        return '/transactional-viber'
    }
    if (hasPrivilege(PRIVILEGES.TRANSACTIONAL_SMS_READ_PRIVILEGE, privileges)) {
        return '/transactional-sms'
    }
    if (hasPrivilege(PRIVILEGES.USER_READ_PRIVILEGE, privileges)) {
        return '/users'
    }
}

router.beforeEach(async (to, from, next) => {
    const token = store.getters.getToken
    if (token && !jwtUtil.isJwtExpired(token) && !store.getters.getCurrentUser) {
        await store.dispatch('setCurrentUser')
    }
    const privileges = store.getters.getCurrentUserPrivileges
    if ((!token || (token && jwtUtil.isJwtExpired(token))) && to.name !== 'Login') {
        store.dispatch('logout').then(() => next({ path: '/login' }))
    } else if (to.meta.requiresAdmin && store.getters.getCurrentUser?.role?.role === ROLES.USER) {
        next({ path: getDefaultPath(privileges) })
    } else if (token && !jwtUtil.isJwtExpired(token) && to.name === 'Login') {
        next({ path: getDefaultPath(privileges) })
    } else if (!token && to.name === 'Login') {
        next()
    } else if (to.meta.requiresPrivilege && !hasPrivilege(to.meta.requiresPrivilege, privileges) && from.path === '/login') {
        next({ path: getDefaultPath(privileges) })
    } else if (to.meta.requiresPrivilege && !hasPrivilege(to.meta.requiresPrivilege, privileges)) {
        return false
    } else {
        next()
    }
})

export default router
